// useYjsDocument — React hook wrapping the Yjs blocks connection.
//
// Surfaces the live document as a `blockProps` snapshot (block_id → props)
// plus a `setBlockProperties` writer, mirroring the shape RuntimeContext
// already exposes via the REST `DocSync`. This is the integration seam:
// document mode would consume this hook instead of (or alongside) DocSync
// to gain real-time multi-user collaboration. See yjsClient.ts.

import { useCallback, useEffect, useRef, useState } from 'react'
import { connectYjsDocument, type YjsBlocksConnection } from './yjsClient'

export interface UseYjsDocumentResult {
  /** True while the WebSocket is open. */
  connected: boolean
  /** Live snapshot of every block's properties, keyed by block id. */
  blockProps: Record<string, Record<string, unknown>>
  /** Merge a property patch onto a block (CRDT-merged + broadcast). */
  setBlockProperties: (blockId: string, props: Record<string, unknown>) => void
}

export interface UseYjsDocumentOpts {
  publicToken?: string
  /** Gate the connection (e.g. only in document mode). Default true. */
  enabled?: boolean
}

export function useYjsDocument(
  documentId: string | null,
  opts: UseYjsDocumentOpts = {},
): UseYjsDocumentResult {
  const { publicToken, enabled = true } = opts
  const [connected, setConnected] = useState(false)
  const [blockProps, setBlockProps] = useState<Record<string, Record<string, unknown>>>({})
  const connRef = useRef<YjsBlocksConnection | null>(null)

  useEffect(() => {
    if (!documentId || !enabled) return

    const snapshot = () => {
      const conn = connRef.current
      if (!conn) return
      const out: Record<string, Record<string, unknown>> = {}
      conn.blocks.forEach((blockMap, blockId) => {
        out[blockId] = blockMap.toJSON() as Record<string, unknown>
      })
      setBlockProps(out)
    }

    const conn = connectYjsDocument(documentId, {
      publicToken,
      onStatus: setConnected,
      onBlocksChange: snapshot,
    })
    connRef.current = conn
    snapshot()  // initial; refreshes again once sync-step-2 lands

    return () => {
      conn.close()
      connRef.current = null
      setConnected(false)
      setBlockProps({})
    }
  }, [documentId, enabled, publicToken])

  const setBlockProperties = useCallback((blockId: string, props: Record<string, unknown>) => {
    connRef.current?.setBlockProperties(blockId, props)
  }, [])

  return { connected, blockProps, setBlockProperties }
}
